import {
  MaterialReactTable,
  type MRT_ColumnDef,
  type MRT_TableOptions,
  useMaterialReactTable,
} from "material-react-table";
import {
  CouponDiscountType,
  CouponStatusFilter,
  type CouponDto,
} from "Infrastructure/Api/Api";
import { useTableLocalization } from "Utils/TableLocalizationUtils";
import { formatCurrency, getRegionCurrency } from "Utils/CurrencyUtils";
import { formatDate } from "Utils/DateUtils";
import { useMemo } from "react";
import { Box, Chip, IconButton, Tooltip } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { CopyToClipboard } from "Components/Shared/CopyToClipboard";
import { Resources, useResource } from "Translations/Resources";

const RESOURCES = Resources.Admin.Coupons;

type Props = {
  data: Array<CouponDto>;
  isLoading: boolean;
  onDelete: (couponID: number) => void;
  onRowClick: (coupon: CouponDto) => void;
  rowCount: number;
  tableProps: Partial<MRT_TableOptions<CouponDto>>;
};

export const CouponListTable = ({
  data,
  isLoading,
  onDelete,
  onRowClick,
  rowCount,
  tableProps,
}: Props) => {
  const { t } = useResource();
  const localization = useTableLocalization();

  const columns = useMemo<Array<MRT_ColumnDef<CouponDto>>>(
    () => [
      {
        accessorKey: "code",
        header: t(RESOURCES.Table.Code),
        size: 150,
      },
      {
        accessorKey: "region",
        header: t(RESOURCES.Table.Region),
        size: 110,
      },
      {
        accessorKey: "discount",
        header: t(RESOURCES.Table.Discount),
        size: 170,
        filterVariant: "select",
        filterSelectOptions: [
          {
            label: t(RESOURCES.Form.Percentage),
            value: CouponDiscountType.Percentage,
          },
          {
            label: t(RESOURCES.Form.FixedAmount),
            value: CouponDiscountType.FixedAmount,
          },
        ],
        Cell: ({ row }) => {
          const { discount, discountType } = row.original;
          return discountType === CouponDiscountType.Percentage
            ? `${discount} %`
            : formatCurrency({
                amount: discount,
                currencyCode: getRegionCurrency(row.original.region),
              });
        },
      },
      {
        accessorKey: "expirationDate",
        header: t(RESOURCES.Table.Expiration),
        size: 170,
        filterVariant: "date",
        Cell: ({ row }) => formatDate(row.original.expirationDate),
      },
      {
        id: "usage",
        header: t(RESOURCES.Table.Usage),
        size: 130,
        enableColumnFilter: false,
        accessorFn: (row) => {
          const used = row.initialStock - row.currentStock;
          return `${used} / ${row.initialStock}`;
        },
        Cell: ({ row }) => {
          const { initialStock, currentStock } = row.original;
          const used = initialStock - currentStock;
          return `${used} / ${initialStock}`;
        },
      },
      {
        id: "status",
        header: t(RESOURCES.Table.Status),
        size: 120,
        enableSorting: false,
        accessorFn: (row) => {
          const isExpired = new Date(row.expirationDate) <= new Date();
          if (isExpired) {
            return t(RESOURCES.Status.Expired);
          }
          if (row.currentStock <= 0) {
            return t(RESOURCES.Status.OutOfStock);
          }
          return t(RESOURCES.Status.Active);
        },
        filterVariant: "select",
        filterSelectOptions: [
          {
            label: t(RESOURCES.Status.Active),
            value: CouponStatusFilter.Active,
          },
          {
            label: t(RESOURCES.Status.Expired),
            value: CouponStatusFilter.Expired,
          },
          {
            label: t(RESOURCES.Status.OutOfStock),
            value: CouponStatusFilter.OutOfStock,
          },
        ],
        Cell: ({ row }) => {
          const { expirationDate, currentStock } = row.original;
          const isExpired = new Date(expirationDate) <= new Date();
          if (isExpired) {
            return <Chip label={t(RESOURCES.Status.Expired)} size="small" />;
          }
          if (currentStock <= 0) {
            return (
              <Chip
                label={t(RESOURCES.Status.OutOfStock)}
                size="small"
                color="error"
              />
            );
          }
          return (
            <Chip
              label={t(RESOURCES.Status.Active)}
              size="small"
              color="success"
            />
          );
        },
      },
      {
        header: "",
        id: "actions",
        size: 100,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => (
          <Box display="flex" gap={0.5} onClick={(e) => e.stopPropagation()}>
            <CopyToClipboard
              text={row.original.code}
              accessibilityLabel={t(RESOURCES.Table.CopyCode)}
              hoverMessage={t(RESOURCES.Table.CopyCode)}
              successMessage={t(RESOURCES.Table.CodeCopied)}
            />
            <Tooltip title={t(RESOURCES.Table.Delete)}>
              <IconButton
                size="small"
                color="error"
                onClick={() => onDelete(row.original.couponID)}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        ),
      },
    ],
    [t, onDelete],
  );

  const table = useMaterialReactTable({
    ...tableProps,
    data,
    columns,
    rowCount,
    localization,
    enableColumnResizing: true,
    enableColumnFilters: true,
    columnResizeMode: "onChange",
    initialState: {
      ...tableProps.initialState,
      density: "compact",
    },
    state: {
      ...tableProps.state,
      isLoading,
    },
    muiTableBodyRowProps: ({ row }) => ({
      onClick: () => onRowClick(row.original),
      sx: { cursor: "pointer" },
    }),
    muiTablePaperProps: {
      sx: { borderRadius: "10px" },
    },
  });

  return <MaterialReactTable table={table} />;
};
